import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// Keys we own in browser storage. Cleared on logout to fully reset app state.
// (We intentionally do NOT wipe the entire storage — cookie-consent state and
//  admin session key are intentionally preserved unless explicitly listed here.)
const CLIENT_AUTH_KEYS = [
  'aprof.admin.apiKey',
  'aprof.user',
  'aprof.session',
];

const clearClientAuthState = () => {
  try {
    CLIENT_AUTH_KEYS.forEach((k) => {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    });
    // Belt-and-braces: purge any session-scoped admin api-key values (used by admin pages)
    Object.keys(sessionStorage).forEach((k) => {
      if (k.startsWith('aprof.admin')) sessionStorage.removeItem(k);
    });
  } catch {
    // storage may be blocked in private mode — silently ignore
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Emergent's auth proxy uses Google One Tap (GSI). One Tap silently auto-selects the
  // last-used Google account and IGNORES OAuth-flow parameters like `prompt=select_account`.
  // So we keep the standard URL clean and, for "switch account", route through Google's
  // native AccountChooser first (see loginWithChooser).
  const buildAuthUrl = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    return `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const login = () => {
    window.location.href = buildAuthUrl();
  };

  // "Choose another Google account" — forces Google's native account picker to appear
  // BEFORE the Emergent Auth proxy loads. This does NOT sign the user out of any
  // Google service; it just lets them pick which account to authenticate with.
  // If Google's chooser is bypassed too, we fall back to the direct auth URL.
  const loginWithChooser = () => {
    const emergentUrl = buildAuthUrl();
    // Google's AccountChooser shows the picker for any Google accounts the browser knows
    // about, then continues to the URL. `authuser=-1` explicitly disables the default
    // account hint so the picker is always shown.
    window.location.href =
      `https://accounts.google.com/AccountChooser?continue=${encodeURIComponent(emergentUrl)}&authuser=-1`;
  };

  const logout = async ({ redirect = true } = {}) => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // Even if the network call fails, we still clear client-side state below.
      console.error('Logout error:', e);
    }
    // Wipe React auth state
    setUser(null);
    // Wipe client-side storage
    clearClientAuthState();
    // Full page navigation so React tree is destroyed and any in-memory
    // component-level caches (e.g. professionalProfile in Navbar) are reset.
    if (redirect) {
      window.location.replace('/login');
    }
  };

  const setAuthUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, loginWithChooser, logout, checkAuth, setAuthUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};
